import emailjs from '@emailjs/browser';
import config from '../data/config.json';

/**
 * Sanitize string input to plain text only: removes all HTML tags, script markup,
 * non-printable control characters, normalizes line breaks, and trims whitespace.
 */
export const sanitizeInput = (str, maxLength = 1000) => {
    if (!str || typeof str !== 'string') return '';
    return str
        .replace(/<[^>]*>?/gm, '') // strip all HTML tags
        .replace(/&[a-z0-9#]+;/gi, '') // strip HTML entities
        .replace(/\r\n/g, '\n') // normalize CRLF to LF
        .replace(/\n{3,}/g, '\n\n') // collapse 3+ consecutive newlines into max 2
        .replace(/[^\x20-\x7E\n\t]/g, '') // keep printable ASCII, newlines, and tabs only
        .trim()
        .slice(0, maxLength);
};

/**
 * Format current timestamp for email templates.
 */
export const getFormattedTimestamp = () => {
    return new Date().toLocaleString('en-US', {
        dateStyle: 'full',
        timeStyle: 'medium'
    });
};

/**
 * Resolve EmailJS configuration keys (environment variables take precedence over config.json).
 */
const getEmailJSKeys = () => {
    const envServiceId = typeof process !== 'undefined' && process.env ? process.env.REACT_APP_EMAILJS_SERVICE_ID : undefined;
    const envPublicKey = typeof process !== 'undefined' && process.env ? process.env.REACT_APP_EMAILJS_PUBLIC_KEY : undefined;

    const serviceId = envServiceId || config.emailjsServiceId || 'service_a7dmiop';
    const publicKey = envPublicKey || config.emailjsPublicKey || 'WtT3H66AVkw8oXF9G';

    return { serviceId, publicKey };
};

/**
 * Send Contact Us email via EmailJS (Template: template_vjhx5bu)
 */
export const sendContactEmail = async ({ name, email, phone, subject, message }) => {
    const { serviceId, publicKey } = getEmailJSKeys();
    
    const sanitizedName = sanitizeInput(name, 100);
    const sanitizedEmail = sanitizeInput(email, 100);
    const sanitizedPhone = sanitizeInput(phone, 20);
    const sanitizedSubject = sanitizeInput(subject, 150);
    const sanitizedMessage = sanitizeInput(message, 2000);

    const formattedMessage = 
`A new contact inquiry has been submitted through the Global Abroad Web portal.

INQUIRY DETAILS:
----------------------------------------
• Sender Name: ${sanitizedName}
• Email Address: ${sanitizedEmail}
• Phone Number: ${sanitizedPhone}
• Subject: ${sanitizedSubject}

MESSAGE CONTENT:
----------------------------------------
${sanitizedMessage}`;

    const templateParams = {
        title: sanitizedSubject,
        name: sanitizedName,
        time: getFormattedTimestamp(),
        email: sanitizedEmail,
        phone_number: sanitizedPhone,
        message: formattedMessage
    };

    return emailjs.send(
        serviceId,
        'template_vjhx5bu',
        templateParams,
        publicKey || undefined
    );
};

/**
 * Send Consultation / Registration email via EmailJS (Template: template_vpj83sq)
 */
export const sendConsultationEmail = async ({ name, email, phone, studyPlan, prefCountry, isRegistration = false }) => {
    const { serviceId, publicKey } = getEmailJSKeys();

    const sanitizedName = sanitizeInput(name, 100);
    const sanitizedEmail = sanitizeInput(email, 100);
    const sanitizedPhone = sanitizeInput(phone, 20);
    const sanitizedCourse = sanitizeInput(studyPlan, 100);
    const sanitizedCountry = sanitizeInput(prefCountry, 100);

    const actionType = isRegistration ? "student registration application" : "free consultation request";

    const formattedMessage = 
`A new ${actionType} has been submitted through the Global Abroad Web portal.

STUDENT INQUIRY DETAILS:
----------------------------------------
• Full Name: ${sanitizedName}
• Email Address: ${sanitizedEmail}
• Phone Number: ${sanitizedPhone}
• Preferred Destination: ${sanitizedCountry}
• Intended Study Plan: ${sanitizedCourse}`;

    const titleText = `${sanitizedCountry} - ${sanitizedCourse}${isRegistration ? ' (Registration)' : ''}`;

    const templateParams = {
        title: titleText,
        name: sanitizedName,
        time: getFormattedTimestamp(),
        phone: sanitizedPhone,
        country: sanitizedCountry,
        course: sanitizedCourse,
        message: formattedMessage,
        email: sanitizedEmail
    };

    return emailjs.send(
        serviceId,
        'template_vpj83sq',
        templateParams,
        publicKey || undefined
    );
};

/**
 * Send Newsletter Subscription email via EmailJS (Template: template_vjhx5bu)
 */
export const sendNewsletterEmail = async ({ email }) => {
    const { serviceId, publicKey } = getEmailJSKeys();
    
    const sanitizedEmail = sanitizeInput(email, 100);

    const formattedMessage = 
`A new newsletter subscription request has been submitted through the Global Abroad Web portal.

SUBSCRIPTION DETAILS:
----------------------------------------
• Subscriber Email: ${sanitizedEmail}
• Submitted At: ${getFormattedTimestamp()}

The subscriber has requested to receive regular updates, news, and study abroad consultation offers from Global Abroad Student Consultancy.`;

    const templateParams = {
        title: `Newsletter Subscription - ${sanitizedEmail}`,
        name: `Newsletter Subscriber`,
        time: getFormattedTimestamp(),
        email: sanitizedEmail,
        phone_number: 'N/A',
        message: formattedMessage
    };

    return emailjs.send(
        serviceId,
        'template_vjhx5bu',
        templateParams,
        publicKey || undefined
    );
};
